import { useAppDispatch } from "../../store";
import { useCallback, useContext } from "react";
import useFetchAndTrace from "../../fetchWithTrace";
import {
  Board,
  boardLoaded,
  BoardMachinesStates,
  boardNotFound,
  pageFailure,
} from "../../../../reducers/uploadedBoard";
import settings from "../../../../constants/settings";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "./state-machine/reviewBoardFlowLoader";
import { useDebouncedCallback } from "use-debounce";
import { useGetBoardMessages } from "../messages";

export function useRefreshBoardInStore(storeUpdate?: (board: Board) => void) {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const fetchAndTrace = useFetchAndTrace();
  const dispatch = useAppDispatch();
  const { refreshMessages } = useGetBoardMessages(boardId);

  const handleFetchBoard = useCallback(
    async (response: Response) => {
      if (response.ok) {
        const board = (await response.json()) as Board;
        if (storeUpdate !== undefined) {
          storeUpdate(board);
        } else {
          dispatch(boardLoaded({ board }));
        }
      } else if (response.status == 404 && boardId) {
        dispatch(boardNotFound({ boardId }));
      } else {
        throw Error(`Failed to fetch board: status ${response.status}`);
      }
    },
    [dispatch, storeUpdate, boardId],
  );

  const handleFetchBoardError = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);

  const refreshBoard = useCallback(() => {
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}`,
      handleFetchBoard,
      handleFetchBoardError,
    );
  }, [boardId, fetchAndTrace, handleFetchBoard, handleFetchBoardError]);

  const debouncedRefreshBoard = useDebouncedCallback(
    () => {
      refreshBoard();
    },
    2000,
    { leading: true },
  );

  // A single board event fans out into several state update messages, and
  // every open tab for the board receives all of them. Debounce the refresh so
  // each burst costs one messages request per client instead of one per
  // message.
  const debouncedRefreshMessages = useDebouncedCallback(
    () => {
      void refreshMessages();
    },
    2000,
    { leading: true },
  );

  const handleBoardStateUpdates = useCallback(
    (nextStatesData: BoardMachinesStates, states: BoardMachinesStates) => {
      if (nextStatesData.board_state != states.board_state) {
        debouncedRefreshBoard();
      }
      debouncedRefreshMessages();
    },
    [debouncedRefreshBoard, debouncedRefreshMessages],
  );

  return {
    refreshBoard: debouncedRefreshBoard,
    handleBoardStateUpdates,
  };
}
